import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/hooks/use-toast';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2, Clock, AlertCircle, Search, CalendarClock, Check, ArrowRight } from 'lucide-react';
import { Device, deviceStore } from '@/stores/deviceStore';
import MainLayout from '@/components/layouts/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { formatTime } from '@/utils/formatters';
import { Separator } from '@/components/ui/separator';

interface DeviceSchedule {
  onTime: string;
  offTime: string;
  schedulesFound: boolean;
}

interface BatchUpdateStatus {
  total: number;
  current: number;
  success: number;
  failed: number;
  messages: { deviceId: string; message: string; status: 'success' | 'error' }[];
}

// Check if a device is online based on its last update time
const isDeviceOnline = (device: Device): boolean => {
  const status = deviceStore.deviceStatus[device.device_id] || {};
  const s = status.s || {};
  
  if (!s.lt && !s.time) return false;
  
  const lastUpdateTime = new Date(s.lt || s.time || '').getTime();
  const now = Date.now();
  
  // Device is considered online if the last update was within the last 15 minutes
  return now - lastUpdateTime < 15 * 60 * 1000;
};

// Check if a device relay is in ON state
const isDeviceOn = (device: Device): boolean => {
  const status = deviceStore.deviceStatus[device.device_id] || {};
  const s = status.s || {};
  const relay = device.relay;
  
  if (!relay || relay < 1 || relay > 3) return false;
  
  // Check if the relay state is 1 (ON)
  const relayState = s[`r${relay}` as keyof typeof s];
  return relayState === 1;
};

const DeviceSchedulesPage = observer(() => {
  const { t } = useTranslation();
  const { toast } = useToast();
  
  // State for devices and their schedules
  const { devices, deviceStatus, loading, error } = deviceStore;
  const [deviceSchedules, setDeviceSchedules] = useState<Record<string, DeviceSchedule>>({});
  const [schedulesLoading, setSchedulesLoading] = useState<Record<string, boolean>>({});
  const [updateLoading, setUpdateLoading] = useState<Record<string, boolean>>({});
  const [searchTerm, setSearchTerm] = useState('');
  
  // Batch update state
  const [batchModalOpen, setBatchModalOpen] = useState(false);
  const [batchOnTime, setBatchOnTime] = useState('20:30');
  const [batchOffTime, setBatchOffTime] = useState('06:30');
  const [batchUpdateStatus, setBatchUpdateStatus] = useState<BatchUpdateStatus | null>(null);
  const [batchUpdateInProgress, setBatchUpdateInProgress] = useState(false);
  
  // Filter and sort devices
  const devicesList = devices
    .filter(device => {
      // First filter by relay existence
      if (!device.relay || device.relay < 1 || device.relay > 3) return false;
      
      // Then filter by search term if present
      if (!searchTerm.trim()) return true;
      
      const lowerSearchTerm = searchTerm.toLowerCase().trim();
      
      return (
        (device.device_id || '').toLowerCase().includes(lowerSearchTerm) ||
        (device.area || '').toLowerCase().includes(lowerSearchTerm) ||
        (device.point || '').toLowerCase().includes(lowerSearchTerm)
      );
    })
    .sort((a, b) => a.device_id.localeCompare(b.device_id)); // Order by device_id

  // Count devices by status
  const onlineDevicesCount = devicesList.filter(d => isDeviceOnline(d)).length;
  const offlineDevicesCount = devicesList.length - onlineDevicesCount;

  // Parse time from cron expression
  const parseTimeFromCron = (cronExpression: string): string => {
    if (!cronExpression || !cronExpression.includes(' ')) return '--:--';
    
    try {
      const parts = cronExpression.split(' ');
      if (parts.length >= 2) {
        return `${parts[1].padStart(2, '0')}:${parts[0].padStart(2, '0')}`;
      }
    } catch (e) {
      console.error('Error parsing cron time:', e);
    }
    
    return '--:--';
  };

  // Format time from HH:MM to HHMM for commands
  const formatTimeForCommand = (timeString: string): string => {
    return timeString.replace(':', '');
  };

  // Get schedule times for a device
  const getDeviceSchedule = (deviceId: string, relay: number): DeviceSchedule => {
    // Check if we already have it in state
    if (deviceSchedules[deviceId]) {
      return deviceSchedules[deviceId];
    }

    // Default times
    let onTime = '20:30', offTime = '06:30';
    let schedulesFound = false;
    
    // Get from device status
    const status = deviceStatus[deviceId] || {};
    const sc = status.sc || {};
    
    if (sc && Object.keys(sc).length > 0) {
      const onSchedule = sc[`r${relay}o` as keyof typeof sc];
      const offSchedule = sc[`r${relay}c` as keyof typeof sc];
      
      if (onSchedule && typeof onSchedule === 'string') {
        // If it's already in HH:MM format
        if (onSchedule.includes(':')) {
          onTime = onSchedule;
        } 
        // Parse cron expression
        else if (onSchedule.includes(' ')) {
          onTime = parseTimeFromCron(onSchedule);
        }
        schedulesFound = true;
      }
      
      if (offSchedule && typeof offSchedule === 'string') {
        // If it's already in HH:MM format
        if (offSchedule.includes(':')) {
          offTime = offSchedule;
        } 
        // Parse cron expression
        else if (offSchedule.includes(' ')) {
          offTime = parseTimeFromCron(offSchedule);
        }
        schedulesFound = true;
      }
    }
    
    const schedule = { onTime, offTime, schedulesFound };
    
    // Store in state
    setDeviceSchedules(prev => ({
      ...prev,
      [deviceId]: schedule
    }));
    
    return schedule;
  };

  // Fetch device schedules from API
  const fetchDeviceSchedule = async (deviceId: string): Promise<void> => {
    // Skip if already loading
    if (schedulesLoading[deviceId]) return;
    
    setSchedulesLoading(prev => ({ ...prev, [deviceId]: true }));
    
    try {
      // Use mock data for now, you can replace with actual API call
      const sc = await deviceStore.getDeviceSchedules(deviceId);
      
      // If we got schedules back
      if (sc && Object.keys(sc).length > 0) {
        console.log(`Got schedules for ${deviceId}:`, sc);
        
        // Update device status with schedules
        deviceStore.updateDeviceStatus(deviceId, { sc });
        
        // Get the device
        const device = devices.find(d => d.device_id === deviceId);
        if (device && device.relay) {
          // Get and update schedule
          const schedule = getDeviceSchedule(deviceId, device.relay);
          setDeviceSchedules(prev => ({
            ...prev,
            [deviceId]: schedule
          }));
        }
      }
    } catch (error) {
      console.error(`Error fetching schedules for ${deviceId}:`, error);
    } finally {
      setSchedulesLoading(prev => ({ ...prev, [deviceId]: false }));
    }
  };

  // Update schedule for a single device
  const updateDeviceSchedule = async (device: Device, onTime: string, offTime: string): Promise<boolean> => {
    if (!device.relay) return false;
    
    // Format times for command
    const onTimeCmd = formatTimeForCommand(onTime);
    const offTimeCmd = formatTimeForCommand(offTime);
    
    // Create command
    const command = `cron_r${device.relay}o_${onTimeCmd}_r${device.relay}c_${offTimeCmd}`;
    
    try {
      // Send command
      const result = await deviceStore.sendCommand(device.device_id, command);
      
      if (result) {
        // Update schedules in memory
        const sc = { 
          [`r${device.relay}o`]: `${onTimeCmd.substr(2, 2)} ${onTimeCmd.substr(0, 2)} * * *`,
          [`r${device.relay}c`]: `${offTimeCmd.substr(2, 2)} ${offTimeCmd.substr(0, 2)} * * *`
        };
        
        // Update device status with schedules
        deviceStore.updateDeviceStatus(device.device_id, { sc });
        
        // Update local state
        setDeviceSchedules(prev => ({
          ...prev,
          [device.device_id]: { onTime, offTime, schedulesFound: true }
        }));
        
        return true;
      }
      
      return false;
    } catch (error) {
      console.error(`Error updating schedule for ${device.device_id}:`, error);
      return false;
    }
  };

  // Handle schedule update for a single device
  const handleUpdateSchedule = async (device: Device) => {
    if (!device.relay) return;
    
    // Get schedule from state
    const schedule = deviceSchedules[device.device_id];
    if (!schedule) return;
    
    setUpdateLoading(prev => ({ ...prev, [device.device_id]: true }));
    
    try {
      const success = await updateDeviceSchedule(device, schedule.onTime, schedule.offTime);
      
      if (success) {
        toast({
          title: t('deviceSchedules.scheduleUpdated'),
          description: t('deviceSchedules.scheduleUpdateSuccess', { 
            deviceId: device.device_id,
            onTime: schedule.onTime,
            offTime: schedule.offTime 
          }),
        });
      } else {
        toast({
          title: t('deviceSchedules.updateFailed'),
          description: t('deviceSchedules.failedToUpdateSchedule'),
          variant: "destructive"
        });
      }
    } catch (error: any) {
      toast({
        title: t('deviceSchedules.updateFailed'),
        description: error.message || t('deviceSchedules.failedToUpdateSchedule'),
        variant: "destructive"
      });
    } finally {
      setUpdateLoading(prev => ({ ...prev, [device.device_id]: false }));
    }
  };

  // Perform batch update
  const performBatchUpdate = async () => {
    // Validate inputs
    if (!batchOnTime || !batchOffTime) {
      toast({
        title: t('deviceSchedules.inputError'),
        description: t('deviceSchedules.enterBothTimes'),
        variant: "destructive"
      });
      return;
    }
    
    // Get devices with relays
    const validDevices = devicesList.filter(device => 
      device.relay && device.relay >= 1 && device.relay <= 3
    );
    
    if (validDevices.length === 0) {
      toast({
        title: t('deviceSchedules.noDevices'),
        description: t('deviceSchedules.noDevicesWithRelays'),
        variant: "destructive"
      });
      return;
    }
    
    // Initialize batch update status
    setBatchUpdateStatus({
      total: validDevices.length,
      current: 0,
      success: 0,
      failed: 0,
      messages: []
    });
    
    setBatchUpdateInProgress(true);
    
    // Process devices
    const batchSize = 5; // Number of devices to process at once
    
    for (let i = 0; i < validDevices.length; i += batchSize) {
      const batch = validDevices.slice(i, i + batchSize);
      
      // Update progress
      setBatchUpdateStatus(prev => ({
        ...prev!,
        current: i
      }));
      
      // Process each device in the batch
      for (const device of batch) {
        try {
          const success = await updateDeviceSchedule(device, batchOnTime, batchOffTime);
          
          setBatchUpdateStatus(prev => ({
            ...prev!,
            current: prev!.current + 1,
            success: success ? prev!.success + 1 : prev!.success,
            failed: success ? prev!.failed : prev!.failed + 1,
            messages: [
              ...prev!.messages,
              {
                deviceId: device.device_id,
                message: success 
                  ? t('deviceSchedules.updateSuccessful')
                  : t('deviceSchedules.updateFailed'),
                status: success ? 'success' : 'error'
              }
            ]
          }));
        } catch (error: any) {
          setBatchUpdateStatus(prev => ({
            ...prev!,
            current: prev!.current + 1,
            failed: prev!.failed + 1,
            messages: [
              ...prev!.messages,
              {
                deviceId: device.device_id,
                message: error.message || t('deviceSchedules.updateFailed'),
                status: 'error'
              }
            ]
          }));
        }
      }
      
      // Small delay between batches
      await new Promise(resolve => setTimeout(resolve, 500));
    }
    
    // Complete the update
    setBatchUpdateInProgress(false);
    
    // Show toast with summary
    const status = batchUpdateStatus!;
    toast({
      title: t('deviceSchedules.batchUpdateComplete'),
      description: t('deviceSchedules.batchUpdateSummary', {
        total: status.total,
        success: status.success,
        failed: status.failed
      }),
      variant: status.failed === 0 ? "default" : "destructive"
    });
  };

  // Handle schedule input change
  const handleScheduleChange = (deviceId: string, type: 'onTime' | 'offTime', value: string) => {
    setDeviceSchedules(prev => ({
      ...prev,
      [deviceId]: {
        ...prev[deviceId],
        [type]: value
      }
    }));
  };

  // Reset batch update state
  const resetBatchUpdate = () => {
    setBatchUpdateStatus(null);
    setBatchUpdateInProgress(false);
  };

  // Load devices and schedules when component mounts
  useEffect(() => {
    // Call the method directly on the store to preserve context
    deviceStore.loadDevices();
    
    // Start polling for updates
    const interval = setInterval(() => {
      deviceStore.loadDevices();
    }, 60000); // every minute
    
    return () => clearInterval(interval);
  }, []);

  // Fetch schedules for devices that don't have them
  useEffect(() => {
    devicesList.forEach(device => {
      // If no schedules found, fetch them
      const status = deviceStatus[device.device_id] || {};
      const schedule = deviceSchedules[device.device_id];
      
      if (
        (!status.sc || Object.keys(status.sc).length === 0) && 
        (!schedule || !schedule.schedulesFound)
      ) {
        fetchDeviceSchedule(device.device_id);
      }
    });
  }, [devicesList, deviceStatus]);

  return (
    <MainLayout>
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <CardTitle>{t('deviceSchedules.title', 'Device Schedules')}</CardTitle>
                <CardDescription className="mt-1">{t('deviceSchedules.description', 'Manage schedules for all your devices')}</CardDescription>
              </div>
              <Button 
                onClick={() => setBatchModalOpen(true)}
                className="flex items-center gap-2 w-full md:w-auto"
              >
                <Clock className="h-4 w-4" />
                {t('deviceSchedules.batchUpdate', 'Batch Update All')}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {loading && (
              <div className="flex justify-center items-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            )}

            {error && (
              <Alert variant="destructive" className="mb-4">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {!loading && !error && (
              <div className="relative mb-4">
                <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder={t('deviceSchedules.searchDevices', 'Search devices by ID, area, or point...')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8"
                />
              </div>
            )}

            {!loading && !error && devicesList.length === 0 && (
              <div className="text-center py-12 text-muted-foreground">
                {searchTerm ? 
                  t('deviceSchedules.noDevicesFoundForSearch', 'No devices matching your search') :
                  t('deviceSchedules.noDevicesFound', 'No devices with relay controls found')
                }
              </div>
            )}

            {!loading && !error && devicesList.length > 0 && (
              <>
                <div className="mb-4">
                  <div className="flex items-center gap-4 mb-2 flex-wrap">
                    <div className="flex items-center">
                      <div className="w-3 h-3 rounded-full bg-white border border-blue-500 mr-2"></div>
                      <span>{t('deviceMap.onlineDevices', 'Online')}: {onlineDevicesCount}</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-3 h-3 rounded-full bg-white border border-gray-400 mr-2"></div>
                      <span>{t('deviceMap.offlineDevices', 'Offline')}: {offlineDevicesCount}</span>
                    </div>
                    <div className="flex items-center ml-auto">
                      <span>{t('deviceMap.totalDevices', 'Total')}: {devices.length}</span>
                    </div>
                  </div>
                </div>
                
                {/* Desktop Table View */}
                <div className="rounded-md border hidden md:block">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('deviceSchedules.deviceId', 'Device ID')}</TableHead>
                        <TableHead>{t('deviceSchedules.area', 'Area')}</TableHead>
                        <TableHead>{t('deviceSchedules.relay', 'Relay')}</TableHead>
                        <TableHead>{t('deviceSchedules.onTime', 'ON Time')}</TableHead>
                        <TableHead>{t('deviceSchedules.offTime', 'OFF Time')}</TableHead>
                        <TableHead>{t('deviceSchedules.actions', 'Actions')}</TableHead>
                        <TableHead>{t('deviceSchedules.status', 'Status')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {devicesList.map(device => {
                        const schedule = getDeviceSchedule(device.device_id, device.relay!);
                        const isLoading = schedulesLoading[device.device_id] || updateLoading[device.device_id];
                        const deviceIsOnline = isDeviceOnline(device);
                        
                        return (
                          <TableRow key={device.device_id}>
                            <TableCell className="font-medium">{device.device_id}</TableCell>
                            <TableCell>{device.area || '-'}</TableCell>
                            <TableCell>{device.relay}</TableCell>
                            <TableCell>
                              <Input
                                type="time"
                                value={schedule.onTime}
                                onChange={(e) => handleScheduleChange(device.device_id, 'onTime', e.target.value)}
                                className="w-28"
                                disabled={!deviceIsOnline || isLoading}
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                type="time"
                                value={schedule.offTime}
                                onChange={(e) => handleScheduleChange(device.device_id, 'offTime', e.target.value)}
                                className="w-28"
                                disabled={!deviceIsOnline || isLoading}
                              />
                            </TableCell>
                            <TableCell>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleUpdateSchedule(device)}
                                disabled={!deviceIsOnline || isLoading}
                              >
                                {isLoading ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  t('deviceSchedules.update', 'Update')
                                )}
                              </Button>
                            </TableCell>
                            <TableCell>
                              {!deviceIsOnline && (
                                <Badge variant="outline" className="text-gray-500 border-gray-500">
                                  {t('deviceMap.offlineDevices', 'Offline')}
                                </Badge>
                              )}
                              {deviceIsOnline && !schedule.schedulesFound && !isLoading && (
                                <Badge variant="outline" className="text-muted-foreground">
                                  {t('deviceSchedules.noSchedule', 'No Schedule')}
                                </Badge>
                              )}
                              {deviceIsOnline && schedule.schedulesFound && !isLoading && (
                                <Badge variant="outline" className="text-green-600 border-green-600">
                                  {t('deviceSchedules.active', 'Active')}
                                </Badge>
                              )}
                              {deviceIsOnline && isLoading && (
                                <Badge variant="outline" className="text-blue-600 border-blue-600">
                                  {t('deviceSchedules.updating', 'Updating...')}
                                </Badge>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
                
                {/* Mobile Card View */}
                <div className="md:hidden space-y-4">
                  {devicesList.map(device => {
                    const schedule = getDeviceSchedule(device.device_id, device.relay!);
                    const isLoading = schedulesLoading[device.device_id] || updateLoading[device.device_id];
                    const deviceIsOnline = isDeviceOnline(device);
                    const deviceIsOn = isDeviceOn(device);
                    
                    return (
                      <Card key={device.device_id} className={`${deviceIsOnline ? '' : 'opacity-75'}`}>
                        <CardHeader className="pb-2">
                          <div className="flex justify-between items-start">
                            <div>
                              <CardTitle className="text-base">{device.device_id}</CardTitle>
                              <CardDescription>
                                {device.area || '-'} • {t('deviceSchedules.relay', 'Relay')}: {device.relay}
                              </CardDescription>
                            </div>
                            <div>
                              {!deviceIsOnline && (
                                <Badge variant="outline" className="text-gray-500 border-gray-500">
                                  {t('deviceMap.offlineDevices', 'Offline')}
                                </Badge>
                              )}
                              {deviceIsOnline && (
                                <Badge variant={deviceIsOn ? "default" : "secondary"}>
                                  {deviceIsOn 
                                    ? t('deviceSchedules.on', 'ON') 
                                    : t('deviceSchedules.off', 'OFF')}
                                </Badge>
                              )}
                              {deviceIsOnline && !schedule.schedulesFound && !isLoading && (
                                <Badge variant="outline" className="text-muted-foreground ml-2">
                                  {t('deviceSchedules.noSchedule', 'No Schedule')}
                                </Badge>
                              )}
                              {deviceIsOnline && schedule.schedulesFound && !isLoading && (
                                <Badge variant="outline" className="text-green-600 border-green-600 ml-2">
                                  {t('deviceSchedules.active', 'Active')}
                                </Badge>
                              )}
                            </div>
                          </div>
                        </CardHeader>
                        
                        <CardContent>
                          <div className="flex items-center justify-center gap-4 py-2">
                            <div className="space-y-1 text-center">
                              <div className="text-xs text-muted-foreground">{t('deviceSchedules.onTime', 'ON Time')}</div>
                              <Input
                                type="time"
                                value={schedule.onTime}
                                onChange={(e) => handleScheduleChange(device.device_id, 'onTime', e.target.value)}
                                className="text-center touch-manipulation"
                                disabled={!deviceIsOnline || isLoading}
                              />
        </div>
        
                            <ArrowRight className="h-4 w-4 text-muted-foreground" />
                            
                            <div className="space-y-1 text-center">
                              <div className="text-xs text-muted-foreground">{t('deviceSchedules.offTime', 'OFF Time')}</div>
                              <Input
                                type="time"
                                value={schedule.offTime}
                                onChange={(e) => handleScheduleChange(device.device_id, 'offTime', e.target.value)}
                                className="text-center touch-manipulation"
                                disabled={!deviceIsOnline || isLoading}
                              />
                            </div>
                          </div>
                        </CardContent>
                        
                        <CardFooter className="pt-0">
                          <Button
                            className="w-full"
                            onClick={() => handleUpdateSchedule(device)}
                            disabled={!deviceIsOnline || isLoading}
                          >
                            {isLoading ? (
                              <Loader2 className="h-4 w-4 animate-spin mr-2" />
                            ) : (
                              <Check className="h-4 w-4 mr-2" />
                            )}
                            {t('deviceSchedules.update', 'Update Schedule')}
                          </Button>
                        </CardFooter>
                      </Card>
                    );
                  })}
                </div>
              </>
            )}
            
            <div className="text-sm text-muted-foreground mt-4 flex items-center gap-2">
              <CalendarClock className="h-4 w-4" />
              {t('deviceSchedules.lastUpdated', 'Last updated')}: {formatTime(new Date().toISOString())}
        </div>
          </CardContent>
        </Card>
      </div>

      {/* Batch Update Dialog */}
      <Dialog open={batchModalOpen} onOpenChange={setBatchModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('deviceSchedules.batchUpdateTitle', 'Batch Update All Devices')}</DialogTitle>
            <DialogDescription>
              {t('deviceSchedules.batchUpdateDescription', 'Set new schedule times for all devices with relays. Individual devices will be updated one by one.')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-1 sm:grid-cols-4 items-center gap-4">
              <label htmlFor="batchOnTime" className="sm:text-right font-medium sm:col-span-1">
                {t('deviceSchedules.onTime', 'ON Time')}:
              </label>
              <Input
                id="batchOnTime"
                type="time"
                value={batchOnTime}
                onChange={(e) => setBatchOnTime(e.target.value)}
                className="sm:col-span-3 text-center touch-manipulation"
                disabled={batchUpdateInProgress}
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-4 items-center gap-4">
              <label htmlFor="batchOffTime" className="sm:text-right font-medium sm:col-span-1">
                {t('deviceSchedules.offTime', 'OFF Time')}:
              </label>
              <Input
                id="batchOffTime"
                type="time"
                value={batchOffTime}
                onChange={(e) => setBatchOffTime(e.target.value)}
                className="sm:col-span-3 text-center touch-manipulation"
                disabled={batchUpdateInProgress}
              />
            </div>
            
            {batchUpdateStatus && (
              <div className="space-y-2 mt-2">
                <Progress 
                  value={(batchUpdateStatus.current / batchUpdateStatus.total) * 100} 
                  className="h-2"
                />
                <div className="text-xs text-muted-foreground text-center">
                  {batchUpdateStatus.current} / {batchUpdateStatus.total} 
                  {batchUpdateStatus.current === batchUpdateStatus.total ? 
                    ` (${t('deviceSchedules.completed', 'Completed')})` : 
                    ` (${Math.round((batchUpdateStatus.current / batchUpdateStatus.total) * 100)}%)`
                  }
                </div>
                
                {batchUpdateStatus.messages.length > 0 && (
                  <div className="mt-4 border rounded-md p-2 max-h-40 overflow-y-auto text-sm">
                    {batchUpdateStatus.messages.map((msg, index) => (
                      <div key={index} className="py-1 flex items-start gap-2">
                        <span className={msg.status === 'success' ? 'text-green-600' : 'text-red-600'}>
                          {msg.status === 'success' ? '✓' : '✗'}
                        </span>
                        <div>
                          <span className="font-medium">{msg.deviceId}</span>: {msg.message}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {batchUpdateStatus.current === batchUpdateStatus.total && (
                  <div className="mt-2 text-sm">
                    <div className="font-medium">{t('deviceSchedules.summary', 'Summary')}:</div>
                    <div className="grid grid-cols-3 gap-2 mt-1">
                      <div>{t('deviceSchedules.total', 'Total')}: {batchUpdateStatus.total}</div>
                      <div className="text-green-600">{t('deviceSchedules.success', 'Success')}: {batchUpdateStatus.success}</div>
                      <div className="text-red-600">{t('deviceSchedules.failed', 'Failed')}: {batchUpdateStatus.failed}</div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          
          <DialogFooter className="sm:justify-between flex-col sm:flex-row gap-2">
            {batchUpdateStatus && batchUpdateStatus.current === batchUpdateStatus.total ? (
              <div className="flex gap-2 w-full justify-end">
                <DialogClose asChild>
                  <Button variant="outline" onClick={resetBatchUpdate} className="w-full sm:w-auto">
                    {t('deviceSchedules.close', 'Close')}
                  </Button>
                </DialogClose>
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row gap-2 w-full sm:justify-end">
                <Button
                  type="button"
                  variant="destructive"
                  onClick={() => setBatchModalOpen(false)}
                  disabled={batchUpdateInProgress}
                  className="w-full sm:w-auto"
                >
                  {t('deviceSchedules.cancel', 'Cancel')}
                </Button>
                <Button
                  type="button"
                  onClick={performBatchUpdate}
                  disabled={batchUpdateInProgress}
                  className="w-full sm:w-auto"
                >
                  {batchUpdateInProgress ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t('deviceSchedules.updating', 'Updating...')}
                    </>
                  ) : (
                    t('deviceSchedules.updateAllDevices', 'Update All Devices')
                  )}
                </Button>
              </div>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
});

export default DeviceSchedulesPage; 